import { useState } from 'react';
import { useBlockMedia } from './useBlockMedia';
import { MediaImage, Reveal } from '../components/ui';
import { useFaqs } from '../api/queries';

interface PageHeroData {
  eyebrow?: string;
  title?: string;
  mediaId?: string;
  overlayOpacity?: number;
  compact?: boolean;
}

/** Page header. With an image it is a full-bleed hero; without one it is the centred
 *  eyebrow + title used by the inner pages. */
export function PageHeroBlock({ data }: { data: PageHeroData }) {
  const media = useBlockMedia(data.mediaId);

  if (data.compact || !media) {
    return (
      <div className="sz-page-head">
        {data.eyebrow && <p className="sz-eyebrow">{data.eyebrow}</p>}
        <h1>{data.title}</h1>
      </div>
    );
  }

  return (
    <section className="sz-page-hero">
      <MediaImage media={media} alt="" priority className="sz-page-hero__media" sizes="100vw" />
      <div
        className="sz-page-hero__overlay"
        style={{ background: `rgba(0,0,0,${data.overlayOpacity ?? 0.55})` }}
      >
        {data.eyebrow && <p className="sz-eyebrow">{data.eyebrow}</p>}
        <h1 className="sz-page-hero__title">{data.title}</h1>
      </div>
    </section>
  );
}

interface StorySection {
  label?: string;
  title?: string;
  body?: string;
}

/** The Our Story column: label, heading, paragraph, repeated. */
export function StorySectionsBlock({ data }: { data: { sections?: StorySection[] } }) {
  const sections = data.sections ?? [];
  if (sections.length === 0) return null;

  return (
    <section className="sz-story">
      {sections.map((section, index) => (
        <Reveal key={`${section.label}-${index}`} delay={index * 60} as="article">
          <div className="sz-story__item">
            {section.label && <p className="sz-eyebrow sz-eyebrow--tight">{section.label}</p>}
            {section.title && <h3 className="sz-story__title">{section.title}</h3>}
            {section.body && <p className="sz-story__body">{section.body}</p>}
          </div>
        </Reveal>
      ))}
    </section>
  );
}

interface RichTextSection {
  heading?: string;
  body?: string;
}

/** Long-form copy used by the policy pages. */
export function RichTextBlock({
  data,
}: {
  data: { intro?: string; notice?: string; sections?: RichTextSection[] };
}) {
  return (
    <div className="sz-prose">
      {data.intro && <p className="sz-prose__intro">{data.intro}</p>}

      {/* Policy copy shipped with the build is placeholder text; say so rather than let
          it read as reviewed legal wording. */}
      {data.notice && (
        <div className="sz-notice sz-notice--warning" role="note">
          {data.notice}
        </div>
      )}

      {(data.sections ?? []).map((section, index) => (
        <section key={`${section.heading}-${index}`} className="sz-prose__section">
          {section.heading && <h2 className="sz-prose__heading">{section.heading}</h2>}
          {section.body && <p>{section.body}</p>}
        </section>
      ))}
    </div>
  );
}

/** Accordion of published FAQs, loaded from the API. */
export function FaqListBlock({ data }: { data: { title?: string } }) {
  const { data: faqs, isLoading } = useFaqs();
  const [openId, setOpenId] = useState<string | null>(null);

  if (isLoading || !faqs || faqs.length === 0) return null;

  return (
    <section className="sz-faq" aria-labelledby="faq-heading">
      <h2 id="faq-heading" className="sz-faq__title">
        {data.title ?? 'Frequently Asked Questions'}
      </h2>
      <div className="sz-faq__list">
        {faqs.map((faq) => {
          const open = openId === faq.id;
          return (
            <div key={faq.id} className="sz-faq__item">
              <h3 className="sz-faq__question-wrap">
                <button
                  type="button"
                  className="sz-faq__question"
                  aria-expanded={open}
                  aria-controls={`faq-answer-${faq.id}`}
                  onClick={() => setOpenId(open ? null : faq.id)}
                >
                  <span>{faq.question}</span>
                  <span aria-hidden="true" className="sz-faq__icon">
                    {open ? '−' : '+'}
                  </span>
                </button>
              </h3>
              <div id={`faq-answer-${faq.id}`} hidden={!open} className="sz-faq__answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
